import { useIsFetching, useQueryClient } from '@tanstack/react-query';

import { QueryKey } from '../../../constants/index';
import { useCompanyContextReady } from '../../../hooks/company/use-company-context-ready.hook';

// Primary dashboard query is the current cap-table snapshot
// (queryKey: [GET_SNAPSHOT, companyId, includePhantom, capTableMode]); the non-phantom entry drives
// the overview, graph, top-5 list, pool allocation and the valuation graph's Fully Diluted point.
// Secondary queries (valuation history + incorporation event) wait for it so the dashboard does
// not fan out every snapshot request in parallel on first paint.
const currentSnapshotMatchesCompany = (companyId: string, queryKey: readonly unknown[]) =>
  queryKey[0] === QueryKey.GET_SNAPSHOT && queryKey[1] === companyId && !queryKey[2];

export const useDashboardSecondaryQueriesEnabled = (companyId: string) => {
  const isCompanyContextReady = useCompanyContextReady(companyId);
  const queryClient = useQueryClient();

  const isCurrentSnapshotFetching =
    useIsFetching({
      predicate: (query) => currentSnapshotMatchesCompany(companyId, query.queryKey),
    }) > 0;

  const hasCurrentSnapshotData = queryClient
    .getQueryCache()
    .findAll({
      predicate: (query) => currentSnapshotMatchesCompany(companyId, query.queryKey),
    })
    .some((query) => query.state.data !== undefined);

  return isCompanyContextReady && (hasCurrentSnapshotData || !isCurrentSnapshotFetching);
};
